import { UserGroup } from '@repo/validation';

/**
 * One thing a caller may do. Access is granted by capability, never by role: a role is
 * only a named bundle of capabilities (`CAPABILITIES_BY_GROUP`), and a caller holds the
 * union of every role they are in. Content permission tags still apply on top.
 */
export enum Capability {
  // Accounts: disable, enable, reset a password, change MFA, grant or revoke a role
  /** Manage the accounts of students */
  StudentAccountsManage = 'accounts.students.manage',
  /** Manage the accounts of staff other than managers and owners, and create them */
  StaffAccountsManage = 'accounts.staff.manage',
  /** Manage the accounts of managers and owners, and create them */
  PrivilegedAccountsManage = 'accounts.privileged.manage',

  // Students
  /** Find students and see their name, school and year */
  StudentsSearch = 'students.search',
  /** See a student's full record, contact details and course states included */
  StudentsViewDetails = 'students.details.view',
  /** Edit a student's details */
  StudentsEdit = 'students.edit',
  /** Create a student */
  StudentsCreate = 'students.create',
  /** Read and write the notes kept on students */
  StudentNotesManage = 'students.notes.manage',
  /** Export students, staff and enquiries as CSV */
  PeopleExport = 'people.export',

  // Staff
  /** Find staff and see their name and roles */
  StaffSearch = 'staff.search',
  /** See a staff member's full record, contact details included */
  StaffViewDetails = 'staff.details.view',
  /** Edit a staff member's details */
  StaffEdit = 'staff.edit',
  /** Read and write the notes kept on staff */
  StaffNotesManage = 'staff.notes.manage',

  // Enquiries
  /** See enquiries and their notes */
  LeadsView = 'leads.view',
  /** Create, edit and delete enquiries and their notes */
  LeadsEdit = 'leads.edit',
  /** Turn an enquiry into a student */
  LeadsConvert = 'leads.convert',
  /** See every note due for follow-up */
  FollowUpsView = 'followUps.view',
  /** See the notes due for follow-up that are assigned to oneself */
  AssignedFollowUpsView = 'followUps.assigned.view',

  // Activity log
  /** See every class's activity log and retention */
  ActivityLogViewAll = 'activityLog.viewAll',
  /** See the activity log of one's own classes */
  ActivityLogViewOwn = 'activityLog.viewOwn',
  /** Edit the reason recorded against an activity */
  ActivityLogEdit = 'activityLog.edit',

  // Classes
  /** See every class and its lessons */
  ClassesViewAll = 'classes.viewAll',
  /** See the classes one teaches */
  ClassesViewOwn = 'classes.viewOwn',
  /** Create, edit, archive and graduate classes, and add or remove their lessons */
  ClassesManage = 'classes.manage',
  /** Change a class's capacity */
  ClassCapacityEdit = 'classes.capacity.edit',
  /** Enrol and unenrol students in a class, and manage its waitlist */
  ClassEnrolmentsManage = 'classes.enrolments.manage',
  /** Enrol or unenrol a student in a single lesson */
  OneOffEnrolmentsManage = 'lessons.oneOffEnrolments.manage',
  /** Add a break to a class's lessons */
  ClassBreaksAdd = 'classes.breaks.add',

  // Lessons and the roll
  /** See lessons with their enrolled students and notes */
  LessonsView = 'lessons.view',
  /** Edit any lesson, and mark or change its roll on any day */
  LessonsManage = 'lessons.manage',
  /** Mark the roll and record attendance and homework on the lesson's day */
  RollMark = 'roll.mark',
  /** See rolls left unsubmitted and the roll summary */
  RollSummaryView = 'roll.summary.view',
  /** Move any student into another lesson */
  MakeupsManage = 'makeups.manage',
  /** Upload images into lesson and content text */
  ImagesUpload = 'images.upload',
  /** See rooms */
  RoomsView = 'rooms.view',
  /** Browse the notes and videos library, within one's content permissions */
  LibraryBrowse = 'library.browse',

  // A student's own records
  /** See one's own classes and lessons */
  OwnClassesView = 'own.classes.view',
  /** Move oneself into another lesson */
  OwnMakeupsBook = 'own.makeups.book',
  /** See one's own analytics and marked exams */
  OwnAnalyticsView = 'own.analytics.view',
  /** Record one's own progress through the first-visit walkthrough */
  OwnOnboardingUpdate = 'own.onboarding.update',

  // Analytics
  /** See any student's analytics */
  StudentAnalyticsView = 'analytics.students.view',

  // Tutorials
  /** See every course's tutorials and filter them by host */
  TutorialsViewAll = 'tutorials.viewAll',
  /** Create, edit and delete any tutorial, and book anyone into it */
  TutorialsManage = 'tutorials.manage',
  /** Host one's own tutorials and book students into them */
  TutorialsHost = 'tutorials.host',
  /** Book oneself into a tutorial */
  TutorialsBook = 'tutorials.book',

  // Curriculum
  /** See the subjects pages */
  CurriculumView = 'curriculum.view',
  /** Create, edit and delete streams, with their prices and content permissions */
  StreamsManage = 'streams.manage',
  /** Create, edit, reorder and delete lesson templates, with their content permissions */
  LessonTemplatesManage = 'lessonTemplates.manage',
  /** Create a course */
  CoursesCreate = 'courses.create',
  /** Edit a course */
  CoursesEdit = 'courses.edit',
  /** Delete a course */
  CoursesDelete = 'courses.delete',
  /** Create, edit and delete legacy topics and topic groups */
  LegacyTopicsEdit = 'legacyTopics.edit',

  // Library content
  /** Create and edit notes */
  NotesEdit = 'notes.edit',
  /** Create, rename and delete note categories, and delete notes */
  NotesOrganise = 'notes.organise',
  /** Create and edit videos, and replace a video's file */
  VideosEdit = 'videos.edit',
  /** Create, rename and delete video categories and topics, and delete or move videos */
  VideosOrganise = 'videos.organise',

  // Content access
  /** See content permissions, permission groups and who holds them */
  ContentAccessView = 'access.view',
  /** Create and rename permission groups, relabel permissions, and share them with groups */
  ContentAccessEdit = 'access.edit',
  /** Grant students content and permission groups, or take them away */
  StudentContentAccessManage = 'access.students.manage',
  /** Grant staff content and permission groups, or take them away */
  StaffContentAccessManage = 'access.staff.manage',
  /** Delete a permission group */
  PermissionGroupsDelete = 'access.groups.delete',

  // Legacy exams and marking
  /** See exams, their analytics and students' attempts */
  LegacyExamsView = 'legacyExams.view',
  /** Create, edit, duplicate and delete exams, with their questions, criteria and guides */
  LegacyExamsManage = 'legacyExams.manage',
  /** Upload papers, assign, approve and remove marking requests, and delete attempts */
  LegacyMarkingAssign = 'legacyMarking.assign',
  /** Take on, mark and hand back papers */
  LegacyMarking = 'legacyMarking.mark',
  /** Export marked papers as CSV */
  LegacyMarkingExport = 'legacyMarking.export',
  /** Draft a paper's overall feedback with AI */
  LegacyFeedbackGenerate = 'legacyMarking.feedback.generate',

  // Billing and pay
  /** See invoices and what a student owes */
  InvoicesView = 'billing.invoices.view',
  /** Create invoices */
  InvoicesCreate = 'billing.invoices.create',
  /** Set a student's credits to any value */
  CreditsEdit = 'billing.credits.edit',
  /** Clear a student's credits to zero, as when they leave a class */
  CreditsClear = 'billing.credits.clear',
  /** Close a settled course state */
  CourseStatesSettle = 'billing.courseStates.settle',
  /** Edit a student's billing settings and discounts */
  StudentBillingEdit = 'billing.students.edit',
  /** See prices */
  PricesView = 'billing.prices.view',
  /** Create and edit prices */
  PricesEdit = 'billing.prices.edit',
  /** See and export payroll */
  PayrollView = 'payroll.view',
  /** See the timesheet */
  TimesheetView = 'timesheet.view',

  // Schools
  /** See the schools list */
  SchoolsView = 'schools.view',
  /** Create and edit schools */
  SchoolsEdit = 'schools.edit',
  /** Delete a school */
  SchoolsDelete = 'schools.delete',

  // Nile
  /** See source documents */
  NileSourcesView = 'nile.sources.view',
  /** Upload, correct and re-digitise source documents */
  NileSourcesEdit = 'nile.sources.edit',
  /** See questions with their enrichment, runs and faults */
  NileQuestionsView = 'nile.questions.view',
  /** Edit, publish, enrich and re-digitise questions */
  NileQuestionsEdit = 'nile.questions.edit',
  /** See the topic taxonomy, vocabularies and institutions */
  NileTaxonomyView = 'nile.taxonomy.view',

  // Quizzes
  /** See question sets */
  QuestionSetsView = 'quizzes.sets.view',
  /** Compose, edit, publish and archive question sets */
  QuestionSetsEdit = 'quizzes.sets.edit',
  /** See a question set's answer key */
  QuestionSetSolutionsView = 'quizzes.solutions.view',
  /** Run a quiz in a lesson */
  QuizzesRun = 'quizzes.run',
  /** Set a lesson template's preset quiz */
  LessonQuizPresetsSet = 'quizzes.lessonPresets.set',
  /** Upload, read and file students' quiz response sheets */
  QuizSubmissionsManage = 'quizzes.submissions.manage',
  /** See students' quiz attempts */
  QuizAttemptsView = 'quizzes.attempts.view',

  // Other tools
  /** Use the printing tool */
  PrintingUse = 'printing.use',
  /** Preview the walkthrough a new student sees on their first visit */
  OnboardingPreview = 'onboarding.preview',
}

/**
 * What each role grants. Roles carry no hierarchy, so a role lists only what it is for: an
 * owner is expected to hold the manager and admin roles as well, rather than `Owners`
 * restating everything they grant.
 */
export const CAPABILITIES_BY_GROUP: Readonly<Record<UserGroup, readonly Capability[]>> = {
  [UserGroup.Owners]: [
    Capability.PrivilegedAccountsManage,
    Capability.CoursesCreate,
    Capability.CoursesDelete,
    Capability.SchoolsDelete,
  ],
  [UserGroup.Managers]: [
    Capability.StaffAccountsManage,
    Capability.StudentsCreate,
    Capability.PeopleExport,
    Capability.StaffEdit,
    Capability.ClassCapacityEdit,
    Capability.OneOffEnrolmentsManage,
    Capability.TutorialsViewAll,
    Capability.StreamsManage,
    Capability.StaffContentAccessManage,
    Capability.PermissionGroupsDelete,
    Capability.LegacyMarkingExport,
    Capability.SchoolsEdit,
    Capability.OnboardingPreview,
  ],
  [UserGroup.Admins]: [
    Capability.StudentAccountsManage,
    Capability.StudentsSearch,
    Capability.StudentsViewDetails,
    Capability.StudentsEdit,
    Capability.StudentNotesManage,
    Capability.StaffEdit,
    Capability.StaffNotesManage,
    Capability.LeadsEdit,
    Capability.LeadsConvert,
    Capability.FollowUpsView,
    Capability.ActivityLogViewAll,
    Capability.ActivityLogEdit,
    Capability.ClassesViewAll,
    Capability.ClassesManage,
    Capability.ClassEnrolmentsManage,
    Capability.ClassBreaksAdd,
    Capability.LessonsManage,
    Capability.RollSummaryView,
    Capability.MakeupsManage,
    Capability.ImagesUpload,
    Capability.RoomsView,
    Capability.LibraryBrowse,
    Capability.StudentAnalyticsView,
    Capability.TutorialsViewAll,
    Capability.TutorialsManage,
    Capability.CreditsClear,
    Capability.StudentBillingEdit,
    Capability.ContentAccessEdit,
    Capability.StudentContentAccessManage,
    Capability.LegacyMarkingAssign,
    Capability.SchoolsView,
    Capability.PrintingUse,
  ],
  [UserGroup.Teachers]: [
    Capability.StudentsSearch,
    Capability.StaffSearch,
    Capability.ActivityLogViewOwn,
    Capability.ClassesViewOwn,
    Capability.ClassBreaksAdd,
    Capability.RollMark,
    Capability.ImagesUpload,
    Capability.RoomsView,
    Capability.LibraryBrowse,
    Capability.StudentAnalyticsView,
    Capability.TutorialsHost,
    Capability.LegacyExamsView,
    Capability.LegacyMarking,
  ],
  [UserGroup.Students]: [
    Capability.OwnClassesView,
    Capability.OwnMakeupsBook,
    Capability.OwnAnalyticsView,
    Capability.OwnOnboardingUpdate,
    Capability.TutorialsBook,
    Capability.RoomsView,
    Capability.LibraryBrowse,
  ],
  [UserGroup.BillingOfficers]: [
    Capability.StudentsViewDetails,
    Capability.StudentNotesManage,
    Capability.StaffSearch,
    Capability.AssignedFollowUpsView,
    Capability.InvoicesCreate,
    Capability.CreditsEdit,
    Capability.CourseStatesSettle,
    Capability.StudentBillingEdit,
    Capability.PricesEdit,
  ],
  [UserGroup.PayrollOfficers]: [Capability.PayrollView, Capability.TimesheetView],
  [UserGroup.ShiftManagers]: [Capability.TimesheetView],
  [UserGroup.CourseManagers]: [
    Capability.LessonTemplatesManage,
    Capability.CoursesEdit,
    Capability.LegacyTopicsEdit,
    Capability.NotesOrganise,
    Capability.VideosOrganise,
    Capability.LegacyFeedbackGenerate,
    Capability.NileSourcesEdit,
    Capability.NileQuestionsEdit,
  ],
  [UserGroup.CourseDevelopers]: [
    Capability.NotesEdit,
    Capability.VideosEdit,
    Capability.LegacyExamsManage,
    Capability.QuestionSetsEdit,
    Capability.QuizzesRun,
    Capability.LessonQuizPresetsSet,
    Capability.QuizSubmissionsManage,
    Capability.QuizAttemptsView,
  ],
};

/**
 * Capabilities that are no use without others, granted alongside them wherever they are
 * granted, so a role naming one never needs to restate what it rests on.
 */
export const IMPLIED_CAPABILITIES: Readonly<Partial<Record<Capability, readonly Capability[]>>> = {
  [Capability.StaffAccountsManage]: [Capability.StaffViewDetails],
  [Capability.PrivilegedAccountsManage]: [Capability.StaffViewDetails],
  [Capability.StaffViewDetails]: [Capability.StaffSearch],
  [Capability.StaffEdit]: [Capability.StaffViewDetails],
  [Capability.LeadsEdit]: [Capability.LeadsView],
  [Capability.LeadsConvert]: [Capability.LeadsView],
  [Capability.ClassCapacityEdit]: [Capability.ClassesViewAll],
  [Capability.OneOffEnrolmentsManage]: [Capability.ClassesViewAll, Capability.LessonsView],
  [Capability.LessonsManage]: [Capability.LessonsView],
  [Capability.RollMark]: [Capability.LessonsView],
  [Capability.StreamsManage]: [
    Capability.CurriculumView,
    Capability.ClassesViewAll,
    Capability.PricesView,
    Capability.ContentAccessView,
  ],
  [Capability.LessonTemplatesManage]: [Capability.CurriculumView, Capability.ContentAccessView],
  [Capability.CoursesCreate]: [Capability.CurriculumView],
  [Capability.CoursesEdit]: [Capability.CurriculumView],
  [Capability.CoursesDelete]: [Capability.CurriculumView],
  [Capability.LegacyTopicsEdit]: [Capability.CurriculumView],
  [Capability.NotesEdit]: [Capability.CurriculumView, Capability.LibraryBrowse],
  [Capability.NotesOrganise]: [Capability.NotesEdit],
  [Capability.VideosEdit]: [Capability.CurriculumView, Capability.LibraryBrowse],
  [Capability.VideosOrganise]: [Capability.VideosEdit],
  [Capability.ContentAccessView]: [Capability.StaffSearch, Capability.StudentsSearch],
  [Capability.ContentAccessEdit]: [Capability.ContentAccessView],
  [Capability.StudentContentAccessManage]: [Capability.ContentAccessView],
  [Capability.StaffContentAccessManage]: [Capability.ContentAccessView],
  [Capability.PermissionGroupsDelete]: [Capability.ContentAccessView],
  [Capability.LegacyExamsManage]: [
    Capability.LegacyExamsView,
    Capability.CurriculumView,
    Capability.ImagesUpload,
  ],
  [Capability.LegacyMarkingAssign]: [Capability.LegacyExamsView],
  [Capability.InvoicesView]: [Capability.StudentsSearch],
  [Capability.InvoicesCreate]: [Capability.InvoicesView],
  [Capability.CreditsEdit]: [Capability.CreditsClear],
  [Capability.PricesEdit]: [Capability.PricesView],
  [Capability.PayrollView]: [Capability.StaffSearch],
  [Capability.TimesheetView]: [Capability.StaffSearch],
  [Capability.PrintingUse]: [Capability.LessonsView],
  [Capability.SchoolsEdit]: [Capability.SchoolsView],
  [Capability.SchoolsDelete]: [Capability.SchoolsView],
  [Capability.NileSourcesEdit]: [Capability.NileSourcesView],
  [Capability.NileSourcesView]: [Capability.NileTaxonomyView],
  [Capability.NileQuestionsEdit]: [Capability.NileQuestionsView, Capability.NileSourcesView],
  [Capability.NileQuestionsView]: [Capability.NileTaxonomyView],
  [Capability.QuestionSetsEdit]: [Capability.QuestionSetsView],
  [Capability.QuizzesRun]: [Capability.QuestionSetsView, Capability.QuestionSetSolutionsView],
  [Capability.LessonQuizPresetsSet]: [Capability.CurriculumView, Capability.QuestionSetsView],
  [Capability.QuizSubmissionsManage]: [Capability.StudentsSearch, Capability.QuestionSetsView],
  [Capability.QuizAttemptsView]: [
    Capability.StudentsSearch,
    Capability.StudentAnalyticsView,
    Capability.QuestionSetsView,
    Capability.QuestionSetSolutionsView,
  ],
};

/**
 * The capability needed to manage an account holding each role, and to grant or revoke
 * that role. Every staff role but the two that run the business is a staff account.
 */
export const ACCOUNT_CAPABILITY_BY_GROUP: Readonly<Record<UserGroup, Capability>> = {
  [UserGroup.Owners]: Capability.PrivilegedAccountsManage,
  [UserGroup.Managers]: Capability.PrivilegedAccountsManage,
  [UserGroup.Admins]: Capability.StaffAccountsManage,
  [UserGroup.Teachers]: Capability.StaffAccountsManage,
  [UserGroup.Students]: Capability.StudentAccountsManage,
  [UserGroup.BillingOfficers]: Capability.StaffAccountsManage,
  [UserGroup.PayrollOfficers]: Capability.StaffAccountsManage,
  [UserGroup.ShiftManagers]: Capability.StaffAccountsManage,
  [UserGroup.CourseManagers]: Capability.StaffAccountsManage,
  [UserGroup.CourseDevelopers]: Capability.StaffAccountsManage,
};

const isKnownGroup = (group: unknown): group is UserGroup =>
  Object.values(UserGroup).includes(group as UserGroup);

/**
 * Every capability the given groups grant, implied ones included. A group outside
 * `UserGroup` grants nothing.
 */
export const getCapabilities = (groups: readonly unknown[]): Capability[] => {
  const granted = new Set<Capability>();
  const pending = groups.filter(isKnownGroup).flatMap(group => CAPABILITIES_BY_GROUP[group]);

  while (pending.length > 0) {
    const capability = pending.pop() as Capability;
    if (!granted.has(capability)) {
      granted.add(capability);
      pending.push(...(IMPLIED_CAPABILITIES[capability] ?? []));
    }
  }
  return [...granted];
};

/** Whether the capabilities include at least one of those given. */
export const hasAnyCapability = (
  capabilities: readonly Capability[],
  required: readonly Capability[]
) => required.some(capability => capabilities.includes(capability));

/**
 * Whether a caller may act on an account holding the given groups: they need the account
 * capability of every role it holds, so a role shared with a less privileged one never
 * lowers the bar. A target whose groups cannot all be read is never manageable.
 */
export const canManageAccount = (
  capabilities: readonly Capability[],
  targetGroups: readonly unknown[]
) =>
  targetGroups.length > 0 &&
  targetGroups.every(
    group => isKnownGroup(group) && capabilities.includes(ACCOUNT_CAPABILITY_BY_GROUP[group])
  );

/** Whether a caller may grant or revoke a role. */
export const canAssignGroup = (capabilities: readonly Capability[], group: UserGroup) =>
  capabilities.includes(ACCOUNT_CAPABILITY_BY_GROUP[group]);
